import { BaseFundingSource } from '@/src/common/components/baseFundingSource'
import { CardPaymentsStore as Store } from '@/src/experiences/card-payments/store'
import { usePaymentScreen } from '@/src/experiences/card-payments/hooks/usePaymentScreen'
import { CardPaymentsE2E } from '../e2e'
import { withPaymentMethodsForCardManagement } from '@/src/experiences/card-payments/providePaymentMethods/withPaymentMethodsForCardManagement'
import {
  type Platform,
  provideCardPlatform,
} from '@/src/experiences/card-platform/provideCardPlatform'
import { type FunctionComponent, useMemo } from 'react'
import { useRecurringPaymentStore } from '@/src/experiences/card-payments/store/useRecurringPaymentStore'

export function useAutoPayFundingSourceScreenProps() {
  return usePaymentScreen(
    'autoPay/fundingSource',
    'Select Funding Source',
    '/main/card/card-management/payment/autoPay',
  )
}

const AutoPayFundingSource = withPaymentMethodsForCardManagement((paymentMethods) => {
  const { Modal, routerPush } = useAutoPayFundingSourceScreenProps()

  const {
    data,
    loading,
    closePaymentMethod,
    closingPaymentMethod,
    openPlaidLink,
    plaidLinkLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...rest
  } = paymentMethods

  // if this check fails, then we're not memoizing on _all_ of `memoizedPaymentMethods`'s dependencies (i.e. `paymentMethods`'s properties)
  true satisfies keyof typeof rest extends never ? true : false

  const memoizedPaymentMethods = useMemo(
    () =>
      ({
        data,
        loading,
        closePaymentMethod,
        closingPaymentMethod,
        openPlaidLink,
        plaidLinkLoading,
      }) as const satisfies typeof paymentMethods,
    [data, loading, closePaymentMethod, closingPaymentMethod, openPlaidLink, plaidLinkLoading],
  )

  const FundingSource = useMemo(
    () =>
      provideCardPlatform<FunctionComponent<{ platform: Platform }>>(({ platform, ToWrap }) =>
        platform ? <ToWrap platform={platform} /> : <BaseFundingSource loading />,
      )(({ platform }) => {
        const { account, setAccount } =
          platform === 'HIGHNOTE' ? Store.useLegacyAutoPay() : useRecurringPaymentStore()

        return (
          <BaseFundingSource
            storedAccount={account}
            setStoredAccount={setAccount}
            pushToNextScreen={() =>
              routerPush('/main/(tabs)/card/card-management/payment/autoPay/confirmation')
            }
            testID={CardPaymentsE2E.Ids.Press.Continue}
            paymentMethods={memoizedPaymentMethods}
          />
        )
      }),
    [routerPush, memoizedPaymentMethods],
  )

  return Modal({ children: <FundingSource /> })
})

export const AutoPayFundingSourceScreen = {
  name: 'auto-pay-schedule',
  Screen: AutoPayFundingSource,
} as const
